export interface VariationalField {
    readonly xsMm: Float64Array;
    readonly ysMm: Float64Array;
    readonly zsMm: Float64Array;
    readonly lambda: Float64Array;
    readonly poly: Float64Array;
    readonly centreCount: number;
}

/** Value and gradient at one point in mm: `out = [f, ∂f/∂x, ∂f/∂y, ∂f/∂z]`. */
export function evaluateFieldInto(
    field: VariationalField,
    x: number,
    y: number,
    z: number,
    out: Float64Array
): void {
    const { xsMm, ysMm, zsMm, lambda, poly, centreCount } = field;
    let value = poly[0] + poly[1] * x + poly[2] * y + poly[3] * z;
    let gx = poly[1];
    let gy = poly[2];
    let gz = poly[3];
    for (let i = 0; i < centreCount; i++) {
        const dx = x - xsMm[i];
        const dy = y - ysMm[i];
        const dz = z - zsMm[i];
        const r = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const l = lambda[i];
        value += l * r * r * r;
        const w = 3 * l * r;
        gx += w * dx;
        gy += w * dy;
        gz += w * dz;
    }
    out[0] = value;
    out[1] = gx;
    out[2] = gy;
    out[3] = gz;
}

const fieldScratch = new Float64Array(4);

export function evaluateField(
    field: VariationalField,
    x: number,
    y: number,
    z: number
): number {
    evaluateFieldInto(field, x, y, z, fieldScratch);
    return fieldScratch[0];
}

export interface GridSamplingMm {
    readonly origin: readonly [number, number, number];
    readonly stepX: readonly [number, number, number];
    readonly stepY: readonly [number, number, number];
    readonly stepZ: readonly [number, number, number];
}

export interface VolumeEvaluationRequest {
    readonly field: VariationalField;
    readonly sampling: GridSamplingMm;
    readonly sizePx: readonly [number, number, number];
    /** Voxels per coarse cell along each axis; 1 disables the coarse pass. */
    readonly stride: number;
    readonly marginMm: number;
}

/** Slices `[from, to)` of one pass; `coarse` is the coarse grid a fine job reads. */
export interface EvaluationJob {
    readonly id: number;
    readonly kind: 'coarse' | 'fine';
    readonly request: VolumeEvaluationRequest;
    readonly coarse: Float32Array | null;
    readonly from: number;
    readonly to: number;
}

export interface EvaluationJobResult {
    readonly id: number;
    readonly values: Float32Array;
}

export const DEFAULT_COARSE_STRIDE = 2;

const GRADIENT_EPSILON = 1e-12;

export function cellDiagonalMm(
    sampling: GridSamplingMm,
    stride: number
): number {
    const length = (v: readonly [number, number, number]): number =>
        Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    const sx = length(sampling.stepX);
    const sy = length(sampling.stepY);
    const sz = length(sampling.stepZ);
    return stride * Math.sqrt(sx * sx + sy * sy + sz * sz);
}

/** A coarse pass can decide voxels only when a coarse cell is smaller than the margin the corner estimates are clamped to. */
export function coarsePassApplies(request: VolumeEvaluationRequest): boolean {
    return (
        request.stride > 1 &&
        cellDiagonalMm(request.sampling, request.stride) < request.marginMm
    );
}

export function coarseDimensions(
    sizePx: readonly [number, number, number],
    stride: number
): [number, number, number] {
    const dimension = (n: number): number => Math.floor((n - 1) / stride) + 2;
    return [dimension(sizePx[0]), dimension(sizePx[1]), dimension(sizePx[2])];
}

/** Distance estimate `f / |∇f|` at voxel `(i, j, k)` in mm, positive inside, clamped to the margin. */
function voxelDistance(
    request: VolumeEvaluationRequest
): (i: number, j: number, k: number) => number {
    const { field, marginMm } = request;
    const { origin, stepX, stepY, stepZ } = request.sampling;
    const sample = new Float64Array(4);
    const at = (a: number, i: number, j: number, k: number): number =>
        origin[a] + j * stepY[a] + k * stepZ[a] + i * stepX[a];
    return (i, j, k) => {
        evaluateFieldInto(
            field,
            at(0, i, j, k),
            at(1, i, j, k),
            at(2, i, j, k),
            sample
        );
        const slope = Math.sqrt(
            sample[1] * sample[1] +
                sample[2] * sample[2] +
                sample[3] * sample[3]
        );
        const mmPosInside =
            slope > GRADIENT_EPSILON
                ? -sample[0] / slope
                : -Math.sign(sample[0]) * marginMm;
        return Math.max(-marginMm, Math.min(marginMm, mmPosInside));
    };
}

/** Coarse-grid distances for coarse slices `[ckFrom, ckTo)`, x fastest. */
export function evaluateCoarseSlices(
    request: VolumeEvaluationRequest,
    ckFrom: number,
    ckTo: number
): Float32Array {
    const [nx, ny, nz] = request.sizePx;
    const stride = request.stride;
    const [coarseNx, coarseNy] = coarseDimensions(request.sizePx, stride);
    const distance = voxelDistance(request);
    const out = new Float32Array(coarseNx * coarseNy * (ckTo - ckFrom));
    let index = 0;
    for (let ck = ckFrom; ck < ckTo; ck++) {
        const k = Math.min(nz - 1, ck * stride);
        for (let cj = 0; cj < coarseNy; cj++) {
            const j = Math.min(ny - 1, cj * stride);
            for (let ci = 0; ci < coarseNx; ci++) {
                out[index++] = distance(Math.min(nx - 1, ci * stride), j, k);
            }
        }
    }
    return out;
}

/**
 * Voxel distances for slices `[kFrom, kTo)`, x fastest.
 *
 * With a `coarse` grid, a voxel whose coarse cell has every corner on one
 * side and an estimated distance above the cell diagonal takes the smallest
 * corner distance as a same-sign stand-in. The estimate is first order and
 * can overshoot near necks and saddles, so equivalence with stride 1 is
 * verified on golden phantoms, not proven.
 */
export function evaluateFineSlices(
    request: VolumeEvaluationRequest,
    coarse: Float32Array | null,
    kFrom: number,
    kTo: number
): Float32Array {
    const [nx, ny] = request.sizePx;
    const { stride, marginMm } = request;
    const [coarseNx, coarseNy, coarseNz] = coarseDimensions(
        request.sizePx,
        stride
    );
    const diagonalMm = cellDiagonalMm(request.sampling, stride);
    const distance = voxelDistance(request);
    const out = new Float32Array(nx * ny * (kTo - kFrom));
    let index = 0;
    for (let k = kFrom; k < kTo; k++) {
        const ck = Math.floor(k / stride);
        const ck1 = Math.min(coarseNz - 1, ck + 1);
        for (let j = 0; j < ny; j++) {
            const cj = Math.floor(j / stride);
            const cj1 = Math.min(coarseNy - 1, cj + 1);
            for (let i = 0; i < nx; i++) {
                let decided: number | undefined;
                if (coarse) {
                    const ci = Math.floor(i / stride);
                    const ci1 = Math.min(coarseNx - 1, ci + 1);
                    let minV = Infinity;
                    let maxV = -Infinity;
                    for (let kk = ck; kk <= ck1; kk += ck1 - ck || 1) {
                        for (let jj = cj; jj <= cj1; jj += cj1 - cj || 1) {
                            const base = coarseNx * (jj + coarseNy * kk);
                            for (let ii = ci; ii <= ci1; ii += ci1 - ci || 1) {
                                const v = coarse[base + ii];
                                minV = v < minV ? v : minV;
                                maxV = v > maxV ? v : maxV;
                            }
                        }
                    }
                    const allInside = minV >= 0;
                    const nearest = allInside ? minV : -maxV;
                    if ((allInside || maxV < 0) && nearest > diagonalMm) {
                        decided =
                            (allInside ? 1 : -1) * Math.min(marginMm, nearest);
                    }
                }
                out[index++] = decided ?? distance(i, j, k);
            }
        }
    }
    return out;
}

export function evaluateJob(job: EvaluationJob): Float32Array {
    return job.kind === 'coarse'
        ? evaluateCoarseSlices(job.request, job.from, job.to)
        : evaluateFineSlices(job.request, job.coarse, job.from, job.to);
}
